/**
 * SUNDAY FLOOR — global.js
 * Cross-cutting behavior only. Section-specific interactivity (cart drawer,
 * variant selection, filters, predictive search) lives in its own deferred,
 * lazily-enqueued module next to the section that uses it (Part H budget:
 * <=50KB gzipped site-wide, <=25KB on a PDP).
 *
 * This stage only establishes the pattern: no cart/variant/filter logic
 * exists yet, since chrome and PDP are later build stages.
 */

// Utility: dispatch a cross-component event on document, so sections can
// stay decoupled from each other (e.g. cart drawer opening in response to
// an add-to-cart click that lives in a different section).
export function sfEmit(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(`sf:${name}`, { detail, bubbles: true }));
}

// Utility: set/remove inert + aria-hidden on everything in the page outside
// a given dialog element, by walking from the dialog up to the page root
// and inerting each level's other siblings. Reused by every overlay (menu
// drawer, cart drawer, search sheet, filter sheet, lightbox, email modal)
// per Part I's overlay spec.
export function setBackgroundInert(dialogEl, isInert) {
  const root = document.querySelector('.sf-page-wrapper') || document.body;
  let node = dialogEl;

  while (node && node !== root && node.parentElement) {
    const parent = node.parentElement;
    Array.from(parent.children).forEach((sibling) => {
      if (sibling === node) return;
      if (isInert) {
        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
      } else {
        sibling.removeAttribute('inert');
        sibling.removeAttribute('aria-hidden');
      }
    });
    node = parent;
  }
}

// Utility: simple focus trap for dialogs (menu drawer, cart drawer, search
// sheet, filter sheet, lightbox, email modal all reuse this per Part I).
export function trapFocus(container) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable.length) return () => {};
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function onKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  container.addEventListener('keydown', onKeydown);
  return () => container.removeEventListener('keydown', onKeydown);
}
